import { Search, X } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useFieldDictation } from "@/hooks/useFieldDictation";

export const settingsGroups = ["personal", "routine", "account", "help"] as const;
export type SettingsGroup = typeof settingsGroups[number];
export const normalizeSettingsSearch = (value: string) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase().trim();

export default function SettingsNavigation({ group, query, onGroup, onQuery }: {
  group: SettingsGroup; query: string; onGroup: (group: SettingsGroup) => void; onQuery: (query: string) => void;
}) {
  const { t } = useLanguage();
  const dictation = useFieldDictation();
  return <div className="space-y-4 px-5 pb-6">
    <p className="text-sm leading-relaxed text-muted-foreground">{t("settings.ux.subtitle")}</p>
    <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 focus-within:ring-2 focus-within:ring-primary">
      <Search size={18} aria-hidden="true" className="shrink-0 text-muted-foreground" />
      <input type="search" aria-label={t("settings.ux.search")} placeholder={t("settings.ux.search")} value={query}
        onChange={event => onQuery(event.target.value)} className="h-12 min-w-0 flex-1 bg-transparent text-base outline-none" />
      {query && <button type="button" aria-label={t("settings.ux.clear")} onClick={() => onQuery("")} className="min-h-11 min-w-11 grid place-items-center rounded-lg focus-visible:ring-2 focus-visible:ring-primary"><X size={18} /></button>}
    </div>
    {dictation.button(t("settings.ux.search"), onQuery)}
    <nav aria-label={t("settings.ux.sections")} className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {settingsGroups.map(item => <button key={item} type="button" aria-pressed={!query && group === item} onClick={() => onGroup(item)}
        className={`min-h-12 rounded-xl px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${!query && group === item ? "bg-primary/15 text-primary border border-primary/40" : "bg-card text-foreground border border-border hover:bg-accent"}`}>
        {t(`settings.ux.${item}`)}
      </button>)}
    </nav>
    {query && <p role="status" className="text-sm text-muted-foreground">{t("settings.ux.searchHint")}</p>}
  </div>;
}
